import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import SEO from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PeopleYouMayKnow } from '../components/PeopleYouMayKnow';
import { ConnectButton } from '../components/ConnectButton';
import { FollowButton } from '../components/FollowButton';
import { UserActionsDropdown } from '../components/UserActionsDropdown';
import { API_URL as API } from '../config';
import { Users, UserCheck, UserPlus, MessageCircle, Trash2, UserX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function Network() {
    const [activeTab, setActiveTab] = useState('connections');

    const tabs = ['connections', 'pending', 'following', 'followers'];

    return (
        <Layout>
            <SEO title="My Network" description="Manage your connections, followers and discover new people." />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Main Content */}
                    <div className="flex-grow">
                        <h1 className="font-lexend text-3xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>My Network</h1>

                        {/* Tabs — same pattern as Dashboard */}
                        <div className="flex border-b scrollbar-hide overflow-x-auto whitespace-nowrap mb-6" style={{ borderColor: 'var(--border-subtle)' }}>
                        {tabs.map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className="px-4 py-2 border-b-2 font-medium text-sm transition-colors capitalize"
                                    style={{
                                        borderColor: activeTab === tab ? 'var(--text-primary)' : 'transparent',
                                        color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-muted)',
                                        outline: 'none',
                                    }}
                                >
                                    {tab === 'pending' ? 'Pending Requests' : tab}
                                </button>
                            ))}
                        </div>

                        <NetworkList type={activeTab} />
                    </div>

                    {/* Sidebar */}
                    <aside className="w-full lg:w-80 space-y-8">
                        <PeopleYouMayKnow />
                    </aside>
                </div>
            </div>
        </Layout>
    );
}

function NetworkList({ type }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth(); // Assuming useAuth exists and provides user.id

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            let url = '';
            if (type === 'connections') url = `${API}/connections`;
            else if (type === 'pending') url = `${API}/connections/pending`;
            else if (type === 'followers') url = `${API}/follows/followers/${user.id}`;
            else if (type === 'following') url = `${API}/follows/following/${user.id}`;

            const response = await axios.get(url);
            setItems(response.data);
        } catch (error) {
            console.error(`Failed to fetch ${type}:`, error);
        } finally {
            setLoading(false);
        }
    }, [type, user]);

    useEffect(() => {
        if (user) fetchItems();
    }, [user, type, fetchItems]);

    const handleRemoveConnection = async (id) => {
        try {
            await axios.delete(`${API}/connections/${id}`);
            setItems(items.filter(item => item.id !== id));
            toast.success('Connection removed');
        } catch (error) {
            toast.error('Failed to remove connection');
        }
    };

    const handleAction = async (requestId, action) => {
        try {
            await axios.put(`${API}/connections/respond/${requestId}`, { action });
            setItems(items.filter(item => item.id !== requestId));
            toast.success(`Request ${action === 'accept' ? 'accepted' : 'declined'}`);
            // If accepted, we might want to refresh connections tab too, 
            // but for simplicity, the user can just switch tabs.
        } catch (error) {
            toast.error(`Failed to ${action} request`);
        }
    };

    if (loading) return <div className="py-12 text-center" style={{ color: 'var(--text-muted)' }}>Loading {type}...</div>;
    if (items.length === 0) return (
        <div className="py-12 text-center border border-dashed rounded-xl" style={{ color: 'var(--text-muted)', borderColor: 'var(--border-subtle)', backgroundColor: 'var(--bg-card)' }}>
            No {type} found.
        </div>
    );

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map(item => {
                const targetId = type === 'followers' ? item.follower_id : (type === 'following' ? item.followed_id : (item.sender_id === user.id ? item.receiver_id : item.sender_id));
                const targetName = type === 'followers' ? item.follower_name : (type === 'following' ? item.followed_name : (item.sender_id === user.id ? item.receiver_name : item.sender_name));
                
                return (
                    <div key={item.id}
                        className="rounded-xl border p-4 flex items-center justify-between transition-colors shadow"
                        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0"
                                style={{ backgroundColor: 'var(--hover-overlay)', color: 'var(--text-muted)' }}>
                                {targetName?.charAt(0)}
                            </div>
                            <div>
                                <h4 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{targetName}</h4>
                                <Link to={`/profile/${targetId}`}
                                    className="text-xs hover:underline"
                                    style={{ color: 'var(--text-muted)' }}
                                    onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                                >
                                    View Profile
                                </Link>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-1.5 min-w-0">
                            {type === 'pending' && (
                                <div className="flex items-center gap-1.5">
                                    <Button size="sm" className="btn-primary h-8 px-3 text-xs whitespace-nowrap"
                                        onClick={() => handleAction(item.id, 'accept')}>
                                        Accept
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-8 px-2 text-xs whitespace-nowrap"
                                        style={{ color: 'var(--text-muted)' }}
                                        onClick={() => handleAction(item.id, 'decline')}>
                                        Decline
                                    </Button>
                                </div>
                            )}
                            {type === 'connections' && (
                                <Button variant="ghost" size="icon" className="text-red-400 hover:bg-red-400/10 h-8 w-8"
                                    onClick={() => handleRemoveConnection(item.id)}>
                                    <UserX className="w-4 h-4" />
                                </Button>
                            )}
                            <UserActionsDropdown userId={targetId} userName={targetName} isRelated={true} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

